/**
 * Container for Onboarding page
 *
 * @module containers/Onboarding
 */
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { StickyContainer } from 'react-sticky';
import { bindActionCreators, compose } from 'redux';

import * as Actions from '../../actions';
import Footer from '../../components/Footer/Footer';
import Icon from '../../components/Icon/Icon';
import ButtonT3 from '../../components/lib/ButtonT3/ButtonT3';
import { ButtonT3Size, ButtonT3Theme } from '../../components/lib/ButtonT3/constants';
import * as Constants from '../../constants';
import {
  AriaRoles,
  messages as AccessibilityMessages,
} from '../../constants/AccessibilityConstants';
import {
  Icons as OnboardingIcons,
  messages as OnboardingMessages,
  SectionNames,
  StatsOnboardingActions,
} from '../../constants/Onboarding';
import { AsyncDomain } from '../../domains';
import styles from '../../sass/definitions/_breakpoints.scss';
import { addWindowEventListener, removeWindowEventListener } from '../../tools/Globals';
import * as Utils from '../../utils';
import AnonHeader from '../AnonHeader/AnonHeader';

import GenomeSection from './GenomeSection';
import MobileSection from './MobileSection';
import SectionCircle from './SectionCircle';

import './Onboarding.scss';

export class Onboarding extends React.Component {
  /**
   * @property {string} searchTerm - term to search for
   * @property {List} autocompleteResults - immutable list of autocomplete items
   */
  static propTypes = {
    intl: PropTypes.object.isRequired,
    isSearchActive: PropTypes.bool,
    isMobileDevice: PropTypes.bool,
    isPhoneDevice: PropTypes.bool,
    appLink: PropTypes.string,
    isIEorEdge: PropTypes.bool,
    isOldAndroid: PropTypes.bool,
    linkClicked: PropTypes.func.isRequired,
    viewPlansClicked: PropTypes.func.isRequired,
    appStoreClicked: PropTypes.func.isRequired,
    pageRendered: PropTypes.func.isRequired,
  };

  static defaultProps = {
    isSearchActive: false, // The Nav Pandora logo might show when it shouldn't w/o this
  };

  state = {
    searchHasFocus: false,
    searchIsSticky: false,
    plansSectionActive: false,
  };

  componentWillMount() {
    this.props.pageRendered(StatsOnboardingActions.LANDING_PAGE_SHOWN);
    this.mobileSectionRef = React.createRef();
    this.plansSectionRef = React.createRef();
  }

  componentDidMount() {
    addWindowEventListener('scroll', this.handlePlansScroll);
  }

  componentWillUnmount() {
    removeWindowEventListener('scroll', this.handlePlansScroll);
  }

  onPurchaseClicked = url => {
    window.open(url);
  };

  onSearchFocusChange = hasFocus => {
    // Need to make sure this component doesn't scroll when user scrolls search menu
    this.setState({ searchHasFocus: hasFocus });
  };

  onStickyStateChange = val => {
    this.setState({
      searchIsSticky: val,
    });
  };

  onViewPlansClicked = () => {
    this.props.linkClicked(StatsOnboardingActions.CLICK_VIEW_PLANS);
  };

  injectElementsBefore = () => {
    const logoTitle = this.props.intl.formatMessage(AccessibilityMessages.pandoraLogo);
    return (
      <div>
        <div className='Onboarding__logo'>
          <h1>
            <Icon
              className='Onboarding__logo__icon'
              glyph={OnboardingIcons.LogoPandoraIcon}
              title={logoTitle}
            />
          </h1>
        </div>
        <div className='Onboarding__title'>
          <h2>
            <FormattedMessage {...OnboardingMessages.searchTitle} />
          </h2>
        </div>
      </div>
    );
  };

  injectElementsAfter = () => {
    return (
      <div className='Onboarding__explanation'>
        <FormattedMessage {...OnboardingMessages.searchExplanation} />
      </div>
    );
  };

  renderAsyncSearchModal = () => {
    const breakWidthS = parseInt(styles.BREAK_WIDTH_S, 10);
    const isWidthS = window.innerWidth < breakWidthS;
    const stickyOffset = -14;
    const stickyOffsetS = -60;

    const config = {
      bottomViewportPadding: 20,
      injectElementsBefore: this.injectElementsBefore,
      injectElementsAfter: this.injectElementsAfter,
      topOffset: isWidthS ? stickyOffsetS : stickyOffset,
      onStickyStateChange: this.onStickyStateChange,
      sticky: true,
      isCenterPageSearch: true,
      isOnboardingPage: true,
    };

    return <AsyncDomain domain='searchT3' component='SearchModal' {...config} />;
  };

  renderDesktopSearchSection = () => {
    return (
      <div className='Onboarding__contentSection Onboarding__contentSection--search'>
        {this.renderAsyncSearchModal()}
      </div>
    );
  };

  onMobileSplashSectionViewPlansLinkClick = event => {
    this.scrollToSection(event, this.plansSectionRef);
    this.props.viewPlansClicked();
  };

  renderMobileSplashSection = () => {
    return (
      <div className='Onboarding__contentSection Onboarding__contentSection--mobileSplash'>
        <div className='Onboarding__logo'>
          <h1>
            <Icon className='Onboarding__logo__icon' glyph={OnboardingIcons.LogoPandoraIcon} />
          </h1>
        </div>
        <div className='Onboarding__contentSection__subTitle'>
          <h2>
            <FormattedMessage {...OnboardingMessages.mobileSplashTitle} />
          </h2>
        </div>
        <div className='Onboarding__viewPlansCTA'>
          <ButtonT3
            onClick={this.onMobileSplashSectionViewPlansLinkClick}
            size={ButtonT3Size.LARGE}
            isHoverShaded={false}
            theme={ButtonT3Theme.WHITE}
          >
            <FormattedMessage {...OnboardingMessages.viewPlans} />
          </ButtonT3>
        </div>
      </div>
    );
  };

  renderSearchSection = () => {
    if (this.props.isMobileDevice) {
      return this.renderMobileSplashSection();
    }
    return this.renderDesktopSearchSection();
  };

  renderPlansSection = () => {
    const sectionName = SectionNames.PLANS;
    const classes = Utils.Css.createBEM('Onboarding__contentSection', {
      [sectionName]: true,
      plansPhone: this.props.isPhoneDevice,
    });
    const upgradeCardProps = {
      isOldAndroid: this.props.isOldAndroid,
      isMobile: this.props.isMobileDevice,
      isLanding: true,
    };

    return (
      <div className={classes}>
        {!this.props.isMobileDevice && (
          <SectionCircle
            type={sectionName}
            svg={OnboardingIcons.LogoPIcon}
            sectionRef={this.plansSectionRef}
            callback={this.onViewPlansClicked}
            isIEorEdge={this.props.isIEorEdge}
          />
        )}
        <div
          id={sectionName}
          ref={this.plansSectionRef}
          className='Onboarding__contentSection__container'
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
          <h3 className='Onboarding__contentSection__title' ref='heading' tabIndex='0'>
            {!this.props.isPhoneDevice && (
              <FormattedMessage {...OnboardingMessages.signUpSectionTitle} />
            )}
          </h3>
          <div className='Onboarding__plans'>
            <AsyncDomain domain='payments' component='AnonUpgradeCards' {...upgradeCardProps} />
          </div>
          <div className='Onboarding__legalDisclaimer'>
            <AsyncDomain domain='payments' component='OnboardingDisclaimer' />
          </div>
        </div>
      </div>
    );
  };

  handlePlansScroll = () => {
    const plansTop = window.scrollY > this.plansSectionRef.current.offsetTop;
    const plansBottom =
      window.scrollY <
      this.plansSectionRef.current.offsetTop + this.plansSectionRef.current.offsetHeight - 50;
    const isActive = plansTop && plansBottom;

    this.setState({
      plansSectionActive: isActive,
    });
  };

  renderMobileSection = () => {
    return (
      <MobileSection
        mobileSectionRef={this.mobileSectionRef}
        isIEorEdge={this.props.isIEorEdge}
        isMobileDevice={this.props.isMobileDevice}
        linkClicked={this.props.linkClicked}
        appStoreClicked={this.props.appStoreClicked}
        appLink={this.props.appLink}
        appLinkCtaText={OnboardingMessages.listenInTheAppText}
      />
    );
  };

  scrollToSection = (event, ref, callback) => {
    const buttonOffset = this.props.isPhoneDevice ? 75 : 35;
    event.preventDefault();
    // Timeout has to be set here to give section time to load
    setTimeout(() => {
      if (this.props.isIEorEdge || this.props.isOldAndroid) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scroll({
          top: ref.current.offsetTop + buttonOffset,
          behavior: 'smooth',
        });
      }
    }, 200);
    if (callback) {
      callback();
    }
  };

  render() {
    const onboardingClasses = Utils.Css.createBEM('Onboarding', {
      searchFocus: this.state.searchHasFocus,
      sticky: this.state.searchIsSticky,
      notSticky: !this.state.searchIsSticky,
    });
    return (
      <StickyContainer className={onboardingClasses}>
        <AnonHeader
          showLogo={this.state.searchIsSticky || this.props.isSearchActive}
          sticky={true}
          hasStickyState={this.state.searchIsSticky}
          isOnboardingPage={true}
          isBackgroundDark={!this.props.isSearchActive}
          viewPlansOnClick={{
            scroll: this.scrollToSection,
            ref: this.plansSectionRef,
            isActive: this.state.plansSectionActive,
            callback: this.onViewPlansClicked,
          }}
        />
        <main className='Onboarding__main' role={AriaRoles.MAIN}>
          <div className='Onboarding__content'>
            <div className='Onboarding__contentSections'>
              {this.renderSearchSection()}
              {this.renderPlansSection()}
              <GenomeSection
                appLink={this.props.appLink}
                appLinkCtaText={OnboardingMessages.listenInTheAppText}
                linkClicked={this.props.linkClicked}
                appStoreClicked={this.props.appStoreClicked}
                isMobileDevice={this.props.isMobileDevice}
                isIEorEdge={this.props.isIEorEdge}
              />
              {this.renderMobileSection()}
            </div>
          </div>
        </main>
        <Footer hasSignUpSection={true} isBackgroundBlack={true} />
      </StickyContainer>
    );
  }
}

export function mapStateToProps(state) {
  const currentPathname = Utils.Route.getCurrentPathnameFromState(state);
  const appLink = Utils.Route.getMobileLongLink(state, {
    pageUrl: currentPathname,
    feature: Constants.Urls.deepLinkParams.HOMEPAGE,
  });

  return {
    isSearchActive: Utils.UIState.getIsMainSearchActive(state),
    isMobileDevice: Utils.Capabilities.isMobileDevice(state),
    isPhoneDevice: Utils.Capabilities.isMobileWeb(state),
    isIEorEdge: Utils.Capabilities.isIEorEdge(state),
    isOldAndroid: Utils.Capabilities.isOldAndroid(state),
    appLink,
  };
}

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      linkClicked: Actions.UseCases.linkClicked,
      pageRendered: Actions.UseCases.pageRendered,
      viewPlansClicked: Actions.Integrations.viewPlansClicked,
      appStoreClicked: Actions.Integrations.appStoreClicked,
    },
    dispatch,
  );
};

const enhance = compose(connect(mapStateToProps, mapDispatchToProps), injectIntl);

export default enhance(Onboarding);
